import { IStorageAdapter } from './types';
import { LocalStorageHelper, SessionStorageHelper } from './storage';
import { MemoryStorageAdapter } from './storage/memory-storage-adapter';

/**
 * Storage adapter implementation for LocalStorage/SessionStorage helpers
 * This adapter bridges the gap between the service and the app-specific storage implementation
 */
export class StorageAdapter implements IStorageAdapter {
  constructor(
    private readonly storage: {
      getItem(keys: string[]): string | null;
      setItem(key: string[], value: string): void;
      removeItem(keys: string[]): void;
      clear(): void;
    }
  ) {}

  public async getItem(keys: string[]): Promise<string | null> {
    return this.storage.getItem(keys);
  }

  public async setItem(key: string[], value: string): Promise<void> {
    this.storage.setItem(key, value);
  }

  public async removeItem(keys: string[]): Promise<void> {
    this.storage.removeItem(keys);
  }

  public async clear(): Promise<void> {
    this.storage.clear();
  }
}

/**
 * Creates a storage adapter from the built-in LocalStorage helper
 * @param useSessionStorage If true, uses SessionStorage instead of LocalStorage
 */
export function createStorageAdapterFromHelper(
  useSessionStorage = false
): StorageAdapter {
  const storage = useSessionStorage ? SessionStorageHelper : LocalStorageHelper;
  return new StorageAdapter(storage);
}

/**
 * Creates a memory-only storage adapter (no browser storage)
 * Useful for scenarios where persistence is not desired
 * @returns IStorageAdapter instance using in-memory storage
 */
export function createMemoryStorageAdapter(): IStorageAdapter {
  return new MemoryStorageAdapter();
}
